import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import RepeatIcon from "@mui/icons-material/Repeat";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import RestorePageIcon from '@mui/icons-material/RestorePage';
import { ImagePreview } from "./ImagePreview";
import { NoteDetail } from "./NoteDetail";
import { DeleteImage } from "./DeleteImage";
import { ValorationModal } from "./ValorationModal";
import { useImageHandler } from "../../../hooks/tableNotes/useImageHandler";
import { useUpdateSingleNoteOrder } from "../../../hooks/panelClient/useUpdateSingleNoteOrder";
import { GenerateWitnessModal } from "../../sharedComponents/GenerateWitnessModal";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
  restrictToFirstScrollableAncestor,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

export const NotesTable = ({
  classes,
  filteredData,
  date,
  token,
  refresh,
  showDetails,
  selectedIdSection,
  idDocument,
}) => {
  const [selectedNote, setSelectedNote] = useState(null);
  const [modalPreviewOpen, setModalPreviewOpen] = useState(false);
  const [modalDetail, setModalDetail] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [modalValoracion, setModalValoracion] = useState(false);
  const [modalApiWitness, setModalApiWitness] = useState(false);
  const [items, setItems] = useState([]);
  const { updateSingleNoteOrder } = useUpdateSingleNoteOrder(token);

  const showValoration =
    selectedIdSection === 20782 ||
    selectedIdSection === 20784 ||
    selectedIdSection === 20788 ||
    selectedIdSection === 20780;

  useEffect(() => {
    const initializedData = (filteredData || [])
      .map((item, index) => {
        const orden = item.orden_editor ?? item.ORDEN_EDITOR ?? index;
        return {
          ...item,
          orden_editor: orden,
        };
      })
      .sort((a, b) => a.orden_editor - b.orden_editor);

    setItems(initializedData);
  }, [filteredData]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex(
      (item) => item.id_sintesis === active.id
    );
    const newIndex = items.findIndex(
      (item) => item.id_sintesis === over.id
    );

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(items, oldIndex, newIndex).map(
      (item, index) => ({
        ...item,
        orden_editor: index,
      })
    );

    // Actualización optimista de la interfaz
    setItems(newOrder);

    const ids = newOrder.map((item) => item.id_sintesis);
    const idSeccion = selectedIdSection || newOrder[0]?.ID_SECCION || newOrder[0]?.id_seccion;
    const idDocumento = idDocument || newOrder[0]?.ID_DOCUMENTO || newOrder[0]?.id_documento;

    if (idSeccion && idDocumento) {
      try {
        await updateSingleNoteOrder(idSeccion, idDocumento, ids);
      } catch (err) {
        console.error("Failed to reorder notes:", err);
      }
    } else {
      console.error("Faltan idSeccion o idDocumento para reordenar en lote:", { idSeccion, idDocumento });
    }
  };

  const { inputFileRef, handleFileChange, triggerFileInput } = useImageHandler(
    date,
    token,
    refresh,
    selectedNote,
    filteredData
  );

  return (
    <div style={{ height: "100%", overflowX: "scroll", }}>
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[
          restrictToVerticalAxis,
          restrictToWindowEdges,
          restrictToFirstScrollableAncestor,
          restrictToParentElement,
        ]}
      >
        <SortableContext
          items={items.map((item) => item.id_sintesis)}
          strategy={verticalListSortingStrategy}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "15%" }} style={classes.tableTitle}>
                  Título
                </TableCell>
                <TableCell
                  sx={{ width: "30%" }}
                  style={{ display: showDetails ? "table-cell" : "none" }}
                >
                  Síntesis
                </TableCell>
                <TableCell sx={{ width: "15%" }} style={classes.tableTitle}>
                  Testigo
                </TableCell>
                {showValoration && (
                  <TableCell sx={{ width: "15%" }} style={classes.tableTitle}>
                    Valoración
                  </TableCell>
                )}
                <TableCell sx={{ width: "15%" }} style={classes.tableTitle}>
                  Imagen
                </TableCell>
                <TableCell sx={{ width: "10%" }} style={classes.tableTitle}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <SortableRow
                  key={item.id_sintesis}
                  item={item}
                  showDetails={showDetails}
                  showValoration={showValoration}
                  setSelectedNote={setSelectedNote}
                  setModalPreviewOpen={setModalPreviewOpen}
                  setModalDetail={setModalDetail}
                  setModalDelete={setModalDelete}
                  setModalValoracion={setModalValoracion}
                  setModalApiWitness={setModalApiWitness}
                  inputFileRef={inputFileRef}
                  handleFileChange={handleFileChange}
                  triggerFileInput={triggerFileInput}
                />
              ))}
            </TableBody>
          </Table>
        </SortableContext>
      </DndContext>

      <ImagePreview
        open={modalPreviewOpen}
        close={() => setModalPreviewOpen(false)}
        note={selectedNote}
        onDelete={() => setModalDelete(true)}
        handleFileChange={handleFileChange}
        inputFileRef={inputFileRef}
      />
      <NoteDetail
        open={modalDetail}
        close={() => setModalDetail(false)}
        note={selectedNote}
      />
      <DeleteImage
        token={token}
        open={modalDelete}
        close={() => setModalDelete(false)}
        note={selectedNote}
        refresh={refresh}
      />
      <ValorationModal
        open={modalValoracion}
        close={() => setModalValoracion(false)}
        note={selectedNote}
        token={token}
        refresh={refresh}
        selectedIdSection={selectedIdSection}
      />
      <GenerateWitnessModal
        open={modalApiWitness}
        onClose={() => setModalApiWitness(false)}
        note={selectedNote}
      />
    </div>
  );
};

const SortableRow = ({
  item,
  showDetails,
  showValoration,
  setSelectedNote,
  setModalPreviewOpen,
  setModalDetail,
  setModalDelete,
  setModalValoracion,
  setModalApiWitness,
  inputFileRef,
  handleFileChange,
  triggerFileInput,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: item.id_sintesis,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "grab",
    opacity: transform ? 0.5 : 1,
  };

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
    >
      <TableCell>{item.titulo}</TableCell>
      <TableCell sx={{ display: showDetails ? "table-cell" : "none" }}>
        {item.sintesis}
      </TableCell>
      <TableCell>
        {(item?.testigo || []).map((testigo, index) => (
          <div key={index} style={{ marginBottom: '4px' }}>
            <a href={testigo.url} target="_blank" rel="noreferrer noopener">
              {testigo.medio}
            </a>
          </div>
        ))}
      </TableCell>
      {showValoration && (
        <TableCell style={{ textTransform: "capitalize" }}>
          {item.valoracion ? item.valoracion.toLowerCase() : "Sin Valorar"}
        </TableCell>
      )}
      <TableCell>
        {item.imagen === null ? (
          "Sin imagen"
        ) : (
          <Button
            onClick={() => {
              setSelectedNote(item);
              setModalPreviewOpen(true);
            }}
            style={{ borderRadius: "12px" }}
          >
            <img
              src={item.imagen}
              alt={item.titulo}
              style={{
                width: "80px",
                height: "50px",
                objectFit: "cover",
              }}
            />
          </Button>
        )}
      </TableCell>
      <TableCell onPointerDown={stopPropagation}>
        <div style={{ display: "flex", gap: "2px", margin: "5px" }}>
          <Tooltip
            title={item.imagen === null ? "Agregar imagen" : "Cambiar imagen"}
          >
            <IconButton
              color="primary"
              onClick={() => {
                triggerFileInput();
                setSelectedNote(item);
              }}
            >
              {item.imagen === null ? (
                <AddPhotoAlternateIcon />
              ) : (
                <RepeatIcon />
              )}
            </IconButton>
          </Tooltip>
          <input
            ref={inputFileRef}
            type="file"
            style={{ display: "none" }}
            onChange={(e) => handleFileChange(e)}
          />
          <Tooltip title="Eliminar imagen">
            <span>
              <IconButton
                color="primary"
                disabled={item.imagen === null}
                onClick={() => {
                  setSelectedNote(item);
                  setModalDelete(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Ver Detalles">
            <IconButton
              color="primary"
              onClick={() => {
                setSelectedNote(item);
                setModalDetail(true);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Restablecer testigo">
            <IconButton
              color="primary"
              onClick={() => {
                setSelectedNote(item);
                setModalApiWitness(true);
              }}
            >
              <RestorePageIcon />
            </IconButton>
          </Tooltip>
          {showValoration && (
            <Tooltip
              title={`${
                item.valoracion === "Sin Valorar" ? "Añadir" : "Editar"
              } Valoración`}
            >
              <IconButton
                color="primary"
                onClick={() => {
                  setSelectedNote(item);
                  setModalValoracion(true);
                }}
              >
                <ThumbUpIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};
